/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useEnquiryData, useGetUserData } from "../../Utility";
import "./BusinessUsersPage.css";
import toast from "react-hot-toast";
import CommonTable from "../Components/CommonTable";
import {
  onGetEnquiries,
  onEnquiryPost,
  onEnquiryPostReset,
  onEnquiryUpdate,
  onEnquiryUpdateReset,
  onEnquiryView,
} from "../../Store/Slices/enquirySlice";
import { onGetUsers } from "../../Store/Slices/userSlice";
import { formatDate } from "../../utils/UtilityFunctions";
import { getAdminProfile, isSuperAdmin } from "../../utils/auth";
import { cities } from "../../config/appData";

const PAGE_SIZE = 10;

const EVENT_TYPES = [
  { value: "", label: "Select event type" },
  { value: "wedding", label: "Wedding" },
  { value: "engagement", label: "Engagement" },
  { value: "anniversary", label: "Anniversary" },
  { value: "birthday", label: "Birthday" },
  { value: "corporate", label: "Corporate / Conference" },
  { value: "other", label: "Other" },
];

const initialAddForm = () => ({
  name: "",
  email: "",
  phone: "",
  city: "",
  eventdate: "",
  eventtype: "",
  nooguests: "",
  venuePref: "",
  budget: "",
  descrip: "",
  status: "new",
});

function EnquiriesUserPage() {
  const dispatch = useAppDispatch();
  const enquiryData = useEnquiryData();
  const usersData = useGetUserData();
  const profile = getAdminProfile();
  const list = Array.isArray(enquiryData?.getData) ? enquiryData.getData : [];
  const usersList = Array.isArray(usersData) ? usersData : [];
  const adminUsers = usersList
    .filter((u) => {
      const flag = u?.admin_access;
      return flag === true || flag === "true" || flag === 1 || flag === "1";
    })
    .filter((u, index, self) => {
      const id = String(u?.id ?? "").trim();
      if (!id) return false;
      return (
        index ===
        self.findIndex((x) => String(x?.id ?? "").trim() === id)
      );
    });
  const adminAssignedOptions = useMemo(
    () =>
      adminUsers.map((u) => {
        const id = String(u?.id ?? "").trim();
        const name = String(u?.name ?? "").trim();
        const email = String(u?.email ?? "").trim();
        return {
          key: id,
          value: name || email || id,
          label: `${email ? `${email} • ` : ""}ID: ${id}`,
        };
      }),
    [adminUsers],
  );
  const loading = enquiryData?.getLoading ?? false;
  const updateLoading = enquiryData?.updateLoading ?? false;
  const postLoading = enquiryData?.postLoading ?? false;
  const viewLoading = enquiryData?.viewLoading ?? false;
  const [statusModal, setStatusModal] = useState(null);
  const [statusDraft, setStatusDraft] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [remarkDraft, setRemarkDraft] = useState("");
  const [assignedToDraft, setAssignedToDraft] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState(initialAddForm);
  const [addErrors, setAddErrors] = useState({});
  const [deleteModal, setDeleteModal] = useState(null);
  const [isDeletingEnquiry, setIsDeletingEnquiry] = useState(false);
  const displayValue = (value) => {
    if (value == null) return "—";
    const text = String(value).trim();
    return text ? text : "—";
  };

  useEffect(() => {
    dispatch(onEnquiryView({
      seen_by:profile?.id,
    }));
    dispatch(onGetUsers());
  }, [dispatch]);

  useEffect(() => {
    if (enquiryData?.updateStatusCode === "201" || enquiryData?.updateStatusCode === 200) {
      dispatch(onEnquiryUpdateReset());
      if (isDeletingEnquiry) {
        toast.success(enquiryData?.updateMessage || "Enquiry deleted successfully");
      } else {
        toast.success(enquiryData?.updateMessage || "Enquiry updated");
      }
      setIsDeletingEnquiry(false);
      dispatch(onGetEnquiries());
    }
  }, [enquiryData?.updateStatusCode, enquiryData?.updateMessage, isDeletingEnquiry]);

  useEffect(() => {
    const code = enquiryData?.postStatusCode;
    if (code === "201" || code === 201 || code === "200" || code === 200) {
      dispatch(onEnquiryPostReset());
      toast.success(enquiryData?.postMessage || "Enquiry added");
      dispatch(onGetEnquiries());
      setAddModalOpen(false);
      setAddForm(initialAddForm());
      setAddErrors({});
    }
  }, [enquiryData?.postStatusCode, enquiryData?.postMessage]);

  const openStatusModal = (row) => {
    setStatusModal(row);
    setNameDraft(row?.name ?? "");
    setStatusDraft(row?.status ?? "");
    setDescriptionDraft(row?.descrip ?? "");
    setRemarkDraft(row?.remark ?? "");
    setAssignedToDraft(row?.assigned_to != null ? String(row.assigned_to) : "");
  };

  const closeStatusModal = () => {
    if (!updateLoading) {
      setStatusModal(null);
      setNameDraft("");
      setStatusDraft("");
      setDescriptionDraft("");
      setRemarkDraft("");
      setAssignedToDraft("");
    }
  };

  const resolveAssignedToValue = (value) => {
    const typed = String(value ?? "").trim();
    if (!typed) return "";
    const typedNorm = typed.toLowerCase();
    const match = adminUsers.find((u) => {
      const id = String(u?.id ?? "").trim();
      const name = String(u?.name ?? "").trim();
      const email = String(u?.email ?? "").trim();
      const nameWithEmail = name && email ? `${name} (${email})` : "";
      return [id, name, email, nameWithEmail].some(
        (candidate) => candidate && candidate.toLowerCase() === typedNorm,
      );
    });
    return match?.id != null ? String(match.id) : typed;
  };

  const handleStatusSave = () => {
    const name = String(nameDraft ?? "").trim();
    const status = String(statusDraft ?? "").trim() || "new";
    const descrip = String(descriptionDraft ?? "").trim();
    const remark = String(remarkDraft ?? "").trim();
    const assigned_to = resolveAssignedToValue(assignedToDraft);
    if (statusModal?.id != null) {
      setIsDeletingEnquiry(false);
      dispatch(
        onEnquiryUpdate({
          id: statusModal.id,
          name,
          status,
          descrip,
          remark,
          assigned_to,
        }),
      );
      setStatusModal(null);
      setNameDraft("");
      setStatusDraft("");
      setDescriptionDraft("");
      setRemarkDraft("");
      setAssignedToDraft("");
    }
  };

  const openAddModal = () => {
    setAddForm(initialAddForm());
    setAddErrors({});
    setAddModalOpen(true);
  };

  const openDeleteModal = (row) => {
    setDeleteModal(row);
  };

  const closeDeleteModal = () => {
    if (!updateLoading) setDeleteModal(null);
  };

  const handleDeleteEnquiry = () => {
    if (deleteModal?.id == null) return;
    setIsDeletingEnquiry(true);
    dispatch(onEnquiryUpdate({ id: deleteModal.id, is_deleted: true }));
    setDeleteModal(null);
  };

  const closeAddModal = () => {
    if (!postLoading) {
      setAddModalOpen(false);
      setAddForm(initialAddForm());
      setAddErrors({});
    }
  };

  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
    if (addErrors[name]) setAddErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateAddForm = () => {
    const err = {};
    if (!addForm.name?.trim()) err.name = "Name is required";
    if (!addForm.email?.trim()) err.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(addForm.email)) err.email = "Invalid email";
    if (!addForm.phone?.trim()) err.phone = "Phone is required";
    setAddErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    if (!validateAddForm()) return;
    dispatch(
      onEnquiryPost({
        name: addForm.name.trim(),
        email: addForm.email.trim(),
        phone: addForm.phone.trim(),
        city: addForm.city?.trim() || "",
        eventdate: addForm.eventdate || "",
        eventtype: addForm.eventtype || "",
        nooguests: addForm.nooguests?.trim() || "",
        venuePref: addForm.venuePref?.trim() || "",
        budget: addForm.budget?.trim() || "",
        descrip: addForm.descrip?.trim() || "",
        status: addForm.status?.trim() || "new",
      })
    );
  };

  const columns = [
    {
      key: "serial",
      title: "S.No",
      dataIndex: "serial",
      render: (_row, serialNumber) => serialNumber,
    },
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
      render: (row) => displayValue(row.name),
    },
    {
      key: "email",
      title: "Email",
      dataIndex: "email",
      render: (row) => displayValue(row.email),
    },
    {
      key: "city",
      title: "City",
      dataIndex: "city",
      render: (row) => displayValue(row.city),
    },
    {
      key: "phone",
      title: "Phone",
      dataIndex: "phone",
      render: (row) => displayValue(row.phone),
    },
    {
      key: "eventdate",
      title: "Event date",
      dataIndex: "eventdate",
      render: (row) =>
        row.eventdate
          ? new Date(row.eventdate).toLocaleDateString("en-IN")
          : "—",
      exportValue: (row) =>
        row.eventdate
          ? new Date(row.eventdate).toLocaleDateString("en-IN")
          : "",
    },
    {
      key: "eventtype",
      title: "Event type",
      dataIndex: "eventtype",
      render: (row) => displayValue(row.eventtype),
    },
    {
      key: "nooguests",
      title: "No. of guests",
      dataIndex: "nooguests",
      render: (row) => displayValue(row.nooguests),
    },
    {
      key: "venuePref",
      title: "Venue pref",
      dataIndex: "venuePref",
      render: (row) => displayValue(row.venuePref),
    },
    {
      key: "budget",
      title: "Budget",
      dataIndex: "budget",
      render: (row) => displayValue(row.budget),
    },
    {
      key: "Venue/Vendor",
      title: "Venue/Vendor",
      dataIndex: "reference_name",
      render: (row) => displayValue(row.reference_name),
    },
    {
      key: "descrip",
      title: "Description",
      dataIndex: "descrip",
      render: (row) => (
        <button
          type="button"
          className="admin-table-status-btn"
          onClick={() => openStatusModal(row)}
          disabled={updateLoading}
          aria-label={`Update enquiry details for enquiry ${row.id}`}
        >
          {row.descrip
            ? String(row.descrip).slice(0, 40) + (row.descrip.length > 40 ? "…" : "")
            : "—"}
        </button>
      ),
    },
    {
      key: "remark",
      title: "Remark",
      dataIndex: "remark",
      render: (row) => (
        <button
          type="button"
          className="admin-table-status-btn"
          onClick={() => openStatusModal(row)}
          disabled={updateLoading}
          aria-label={`Update remark for enquiry ${row.id}`}
        >
          {displayValue(row.remark)}
        </button>
      ),
    },
    {
      key: "assigned_to",
      title: "Assigned To",
      dataIndex: "assigned_to",
      render: (row) => (
        <button
          type="button"
          className="admin-table-status-btn"
          onClick={() => openStatusModal(row)}
          disabled={updateLoading}
          aria-label={`Update assignee for enquiry ${row.id}`}
        >
          {displayValue(row.assigned_to)}
        </button>
      ),
    },
    {
      key: "created_at",
      title: "Logged Time",
      dataIndex: "created_at",
      render: (row) => (row.created_at ? formatDate(row.created_at) : "—"),
      exportValue: (row) => (row.created_at ? formatDate(row.created_at) : ""),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (row) => (
        <button
          type="button"
          className="admin-table-status-btn"
          onClick={() => openStatusModal(row)}
          disabled={updateLoading}
          aria-label={`Update status for enquiry ${row.id}`}
        >
          {displayValue(row.status)}
        </button>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      render: (row) => (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            type="button"
            className="admin-modal-btn admin-modal-confirm"
            onClick={() => openStatusModal(row)}
            disabled={updateLoading}
            aria-label={`Edit enquiry ${row.id}`}
          >
            Edit
          </button>
          <button
            type="button"
            className="admin-modal-btn admin-modal-reject"
            onClick={() => openDeleteModal(row)}
            disabled={updateLoading}
            aria-label={`Delete enquiry ${row.id}`}
          >
            Delete
          </button>
        </div>
      ),
    },

    
  ];

  return (
    <div className="admin-business-users">
      <div className="admin-page-header-row">
        <h1 className="admin-page-title">Enquiries</h1>
        <button
          type="button"
          className="admin-btn admin-btn-primary"
          onClick={openAddModal}
          disabled={postLoading}
        >
          Add Enquiry
        </button>
      </div>
      <CommonTable
        columns={columns}
        data={list}
        loading={loading || updateLoading || viewLoading || postLoading}
        emptyText="No enquiries found"
        showSearch
        searchPlaceholder="Search all..."
        searchKeys={[
          "id",
          "name",
          "email",
          "city",
          "phone",
          "eventdate",
          "eventtype",
          "nooguests",
          "venuePref",
          "budget",
          "reference_name",
          "descrip",
          "remark",
          "assigned_to",
          "status",
          "created_at",
        ]}
        showCityFilter
        cityKey="city"
        showDateRangeFilter
        dateKey="created_at"
        fromDateLabel="From date"
        toDateLabel="To date"
        pageSize={PAGE_SIZE}
        showPageSizeSelector
        pageSizeOptions={[10, 25, 50, 100]}
        showExport={isSuperAdmin()}
        exportFileName="enquiries"
      />

      {statusModal && (
        <div
          className="admin-modal-overlay"
          onClick={closeStatusModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="enquiry-status-modal-title"
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2 id="enquiry-status-modal-title" className="admin-modal-title">
              Update status
            </h2>
            <p className="admin-modal-text">
              Enquiry #{statusModal.id} – {statusModal.name ?? statusModal.email ?? "—"}
            </p>
            <div className="admin-modal-form-group">
              <label htmlFor="enquiry-name-input" className="admin-modal-label">
                Name
              </label>
              <input
                id="enquiry-name-input"
                type="text"
                className="admin-form-input admin-modal-input"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                placeholder="Full name"
                disabled={updateLoading}
                autoFocus
              />
            </div>
            <div className="admin-modal-form-group">
              <label htmlFor="enquiry-status-input" className="admin-modal-label">
                Status
              </label>
              <input
                id="enquiry-status-input"
                type="text"
                className="admin-form-input admin-modal-input"
                value={statusDraft}
                onChange={(e) => setStatusDraft(e.target.value)}
                placeholder="e.g. New, Contacted, In progress, Closed"
                disabled={updateLoading}
              />
            </div>
            <div className="admin-modal-form-group">
              <label htmlFor="enquiry-remark-input" className="admin-modal-label">
                Remark
              </label>
              <input
                id="enquiry-remark-input"
                type="text"
                className="admin-form-input admin-modal-input"
                value={remarkDraft}
                onChange={(e) => setRemarkDraft(e.target.value)}
                placeholder="Internal remark..."
                disabled={updateLoading}
              />
            </div>
            <div className="admin-modal-form-group">
              <label htmlFor="enquiry-assigned-to-input" className="admin-modal-label">
                Assigned to
              </label>
              <input
                id="enquiry-assigned-to-input"
                type="text"
                className="admin-form-input admin-modal-input"
                value={assignedToDraft}
                onChange={(e) => setAssignedToDraft(e.target.value)}
                placeholder="Type name/email/id (admin suggestions will appear)"
                list="enquiry-assigned-to-options"
                disabled={updateLoading}
              />
              <datalist id="enquiry-assigned-to-options">
                {adminAssignedOptions.map((opt) => (
                  <option key={opt.key} value={opt.value} label={opt.label} />
                ))}
              </datalist>
            </div>
            <div className="admin-modal-form-group">
              <label htmlFor="enquiry-description-input" className="admin-modal-label">
                Description / Message
              </label>
              <textarea
                id="enquiry-description-input"
                className="admin-form-input admin-modal-input"
                value={descriptionDraft}
                onChange={(e) => setDescriptionDraft(e.target.value)}
                placeholder="Enquiry details..."
                rows={4}
                disabled={updateLoading}
              />
            </div>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="admin-modal-btn admin-modal-cancel"
                onClick={closeStatusModal}
                disabled={updateLoading}
              >
                Close
              </button>
              <button
                type="button"
                className="admin-modal-btn admin-modal-confirm"
                onClick={handleStatusSave}
                disabled={updateLoading}
              >
                {updateLoading ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {addModalOpen && (
        <div
          className="admin-modal-overlay"
          onClick={closeAddModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-enquiry-modal-title"
        >
          <div className="admin-modal admin-modal--wide" onClick={(e) => e.stopPropagation()}>
            <h2 id="add-enquiry-modal-title" className="admin-modal-title">
              Add new enquiry
            </h2>
            <form onSubmit={handleAddSubmit} className="admin-modal-form">
              <div className="admin-modal-form-row">
                <div className="admin-modal-form-group">
                  <label className="admin-modal-label" htmlFor="add-enquiry-name">Name *</label>
                  <input
                    id="add-enquiry-name"
                    type="text"
                    name="name"
                    className={`admin-form-input admin-modal-input ${addErrors.name ? "admin-form-input-error" : ""}`}
                    value={addForm.name}
                    onChange={handleAddFormChange}
                    placeholder="Full name"
                    disabled={postLoading}
                  />
                  {addErrors.name && <span className="admin-form-error">{addErrors.name}</span>}
                </div>
                <div className="admin-modal-form-group">
                  <label className="admin-modal-label" htmlFor="add-enquiry-email">Email *</label>
                  <input
                    id="add-enquiry-email"
                    type="email"
                    name="email"
                    className={`admin-form-input admin-modal-input ${addErrors.email ? "admin-form-input-error" : ""}`}
                    value={addForm.email}
                    onChange={handleAddFormChange}
                    placeholder="email@example.com"
                    disabled={postLoading}
                  />
                  {addErrors.email && <span className="admin-form-error">{addErrors.email}</span>}
                </div>
              </div>
              <div className="admin-modal-form-row">
                <div className="admin-modal-form-group">
                  <label className="admin-modal-label" htmlFor="add-enquiry-phone">Phone *</label>
                  <input
                    id="add-enquiry-phone"
                    type="text"
                    name="phone"
                    className={`admin-form-input admin-modal-input ${addErrors.phone ? "admin-form-input-error" : ""}`}
                    value={addForm.phone}
                    onChange={handleAddFormChange}
                    placeholder="10-digit phone"
                    disabled={postLoading}
                  />
                  {addErrors.phone && <span className="admin-form-error">{addErrors.phone}</span>}
                </div>
                <div className="admin-modal-form-group">
                  <label className="admin-modal-label" htmlFor="add-enquiry-city">City</label>
                  <select
                    id="add-enquiry-city"
                    name="city"
                    className="admin-form-input admin-modal-input"
                    value={addForm.city}
                    onChange={handleAddFormChange}
                    disabled={postLoading}
                  >
                    <option value="">Select city</option>
                    {cities.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admin-modal-form-row">
                <div className="admin-modal-form-group">
                  <label className="admin-modal-label" htmlFor="add-enquiry-eventdate">Event date</label>
                  <input
                    id="add-enquiry-eventdate"
                    type="date"
                    name="eventdate"
                    className="admin-form-input admin-modal-input"
                    value={addForm.eventdate}
                    onChange={handleAddFormChange}
                    disabled={postLoading}
                  />
                </div>
                <div className="admin-modal-form-group">
                  <label className="admin-modal-label" htmlFor="add-enquiry-eventtype">Event type</label>
                  <select
                    id="add-enquiry-eventtype"
                    name="eventtype"
                    className="admin-form-input admin-modal-input"
                    value={addForm.eventtype}
                    onChange={handleAddFormChange}
                    disabled={postLoading}
                  >
                    {EVENT_TYPES.map((opt) => (
                      <option key={opt.value || "blank"} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admin-modal-form-row">
                <div className="admin-modal-form-group">
                  <label className="admin-modal-label" htmlFor="add-enquiry-nooguests">No. of guests</label>
                  <input
                    id="add-enquiry-nooguests"
                    type="text"
                    name="nooguests"
                    className="admin-form-input admin-modal-input"
                    value={addForm.nooguests}
                    onChange={handleAddFormChange}
                    placeholder="e.g. 200"
                    disabled={postLoading}
                  />
                </div>
                <div className="admin-modal-form-group">
                  <label className="admin-modal-label" htmlFor="add-enquiry-venuePref">Venue preference</label>
                  <input
                    id="add-enquiry-venuePref"
                    type="text"
                    name="venuePref"
                    className="admin-form-input admin-modal-input"
                    value={addForm.venuePref}
                    onChange={handleAddFormChange}
                    placeholder="e.g. Banquet hall"
                    disabled={postLoading}
                  />
                </div>
              </div>
              <div className="admin-modal-form-row">
                <div className="admin-modal-form-group">
                  <label className="admin-modal-label" htmlFor="add-enquiry-budget">Budget</label>
                  <input
                    id="add-enquiry-budget"
                    type="text"
                    name="budget"
                    className="admin-form-input admin-modal-input"
                    value={addForm.budget}
                    onChange={handleAddFormChange}
                    placeholder="e.g. ₹5L"
                    disabled={postLoading}
                  />
                </div>
                <div className="admin-modal-form-group">
                  <label className="admin-modal-label" htmlFor="add-enquiry-status">Status</label>
                  <input
                    id="add-enquiry-status"
                    type="text"
                    name="status"
                    className="admin-form-input admin-modal-input"
                    value={addForm.status}
                    onChange={handleAddFormChange}
                    placeholder="new"
                    disabled={postLoading}
                  />
                </div>
              </div>
              <div className="admin-modal-form-group">
                <label className="admin-modal-label" htmlFor="add-enquiry-descrip">Description / Message</label>
                <textarea
                  id="add-enquiry-descrip"
                  name="descrip"
                  className="admin-form-input admin-modal-input"
                  value={addForm.descrip}
                  onChange={handleAddFormChange}
                  placeholder="Enquiry details..."
                  rows={3}
                  disabled={postLoading}
                />
              </div>
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-modal-btn admin-modal-cancel"
                  onClick={closeAddModal}
                  disabled={postLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-modal-btn admin-modal-confirm"
                  disabled={postLoading}
                >
                  {postLoading ? "Adding…" : "Add Enquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteModal && (
        <div
          className="admin-modal-overlay"
          onClick={closeDeleteModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-enquiry-modal-title"
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2 id="delete-enquiry-modal-title" className="admin-modal-title">
              Delete enquiry
            </h2>
            <p className="admin-modal-text">
              Are you sure you want to delete enquiry #{deleteModal.id} –{" "}
              {deleteModal.name ?? deleteModal.email ?? "—"}?
            </p>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="admin-modal-btn admin-modal-cancel"
                onClick={closeDeleteModal}
                disabled={updateLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-modal-btn admin-modal-reject"
                onClick={handleDeleteEnquiry}
                disabled={updateLoading}
              >
                {updateLoading ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EnquiriesUserPage;
